import clsx from 'clsx';
import { parseISO } from 'date-fns';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { paths } from '@/app/paths';
import { useExperienceSearch } from '@/app/state/experienceSearch';
import { useOverlays } from '@/app/state/overlays';
import { Button, Calendar, Chip, ChipGroup, Icon, Sheet } from '@/design-system';
import { ns, useT } from '@/i18n';
import { today, toISODate } from '@/lib/clock';
import { ALL_CATEGORIES, type ExperienceFilters } from '@/services/domain';
import { useExperienceCategories, useExperienceCities, useExperiences } from '@/services/queries';
import { messages } from '../i18n';
import { DURATIONS, useSearchLabels } from './labels';
import styles from './ExperienceSearchSheet.module.css';

/** «Où et quand ?» for experiences (screen 15). */
export function ExperienceSearchSheet() {
  const overlays = useOverlays();
  // Mounted only while open, so each opening starts a fresh draft from the current filters.
  return overlays.current === 'experienceSearch' ? <OpenSheet /> : null;
}

function OpenSheet() {
  const overlays = useOverlays();
  const t = useT(messages);
  const tc = useT(ns.common);
  const labels = useSearchLabels();
  const { filters, update, markSearched } = useExperienceSearch();
  const [draft, setDraft] = useState<ExperienceFilters>(filters);
  const [query, setQuery] = useState('');
  const [month, setMonth] = useState(() => today());
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data: cities } = useExperienceCities();
  const { data: categories } = useExperienceCategories(draft.city);
  const { data: results } = useExperiences(draft);

  const patch = (p: Partial<ExperienceFilters>) => setDraft((d) => ({ ...d, ...p }));
  const total = (categories ?? []).reduce((n, c) => n + c.count, 0);
  const shown = (cities ?? []).filter((c) => c.name.toLowerCase().includes(query.trim().toLowerCase()));
  const count = results?.length;

  const submit = () => {
    update(draft);
    markSearched();
    overlays.close();
    if (pathname !== paths.experiences) navigate(paths.experiences);
  };

  return (
    <Sheet
      open
      onClose={overlays.close}
      tone="exp"
      eyebrow={tc('experiences')}
      title={t('search.title')}
      bodyClassName={styles.body}
      footer={
        <>
          <div className={styles.sum}>
            <b>{labels.title(draft)}</b>
            <span className="t-small t-muted">{labels.sub(draft)}</span>
          </div>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.clear}
              onClick={() => setDraft({ ...draft, date: null, category: ALL_CATEGORIES, duration: '' })}
            >
              {t('search.clear')}
            </button>
            <Button variant="saffron" onClick={submit}>
              <Icon name="search" size={18} strokeWidth={2.4} />
              {count === undefined ? t('search.submit') : t('search.show', { count })}
            </Button>
          </div>
        </>
      }
    >
      <div className={styles.a}>
        <span className={clsx('t-label', styles.flush)}>{t('search.where')}</span>
        <label className={styles.where}>
          <Icon name="search" size={18} strokeWidth={2.2} />
          <input
            placeholder={t('search.destination')}
            aria-label={t('search.destinationLabel')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        <span className={clsx('t-label', styles.flushTop)}>{t('search.suggestions')}</span>
        <div className={styles.cities}>
          {shown.map((c) => {
            const on = c.name === draft.city;
            return (
              <button
                key={c.id}
                type="button"
                aria-pressed={on}
                className={clsx(styles.city, on && styles.cityOn)}
                onClick={() => patch({ city: c.name })}
              >
                <span className={styles.pin}>
                  <Icon name="pin" size={17} />
                </span>
                <span className={styles.cityText}>
                  <b>{c.name}</b>
                  <span>{c.caption}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className={styles.cal}>
        <span className="t-label">{t('search.when')}</span>
        <Calendar
          month={month}
          onMonthChange={setMonth}
          start={draft.date ? parseISO(draft.date) : null}
          onPick={(d) => patch({ date: toISODate(d) })}
          isDisabled={(d) => toISODate(d) < toISODate(today())}
          hint={
            <button type="button" className={styles.flex} onClick={() => patch({ date: null })}>
              {t('search.flexible')}
            </button>
          }
        />
      </div>

      <div className={styles.c}>
        <div>
          <span className="t-label">{t('search.category')}</span>
          <ChipGroup wrap>
            <Chip
              selected={draft.category === ALL_CATEGORIES}
              count={total}
              onClick={() => patch({ category: ALL_CATEGORIES })}
            >
              {tc('allCategories')}
            </Chip>
            {(categories ?? []).map((c) => (
              <Chip
                key={c.label}
                selected={draft.category === c.label}
                count={c.count}
                onClick={() => patch({ category: c.label })}
              >
                {c.label}
              </Chip>
            ))}
          </ChipGroup>
        </div>
        <div>
          <span className="t-label">{t('search.duration')}</span>
          <ChipGroup wrap>
            {DURATIONS.map((k) => (
              <Chip key={k || 'all'} selected={draft.duration === k} onClick={() => patch({ duration: k })}>
                {labels.duration(k)}
              </Chip>
            ))}
          </ChipGroup>
        </div>
      </div>
    </Sheet>
  );
}
